import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StyleSheet, Document, Page, Text, View, Image, pdf } from "@react-pdf/renderer";
import html2canvas from "html2canvas";
import { saveAs } from "file-saver";
import moment from "moment";
// Constants
import { WEB_SERVICE_MIMS } from "../../../../config/constants";
// Components
import { MainContainer, DownloadButton, FileImg, DownloadText } from "./styles";
import Loader from "../../../loader";
import UseFetch from "../../../../config/useFetch";
// Assets
import PDFImg from "../../../../assets/icons/ico_pdf.svg";

const DownloadReportPDF = ({ view, showUser = true, plotsRef = {} }) => {
  const [patientUser, setPatientUser] = useState("");
  const [styles] = useState(
    StyleSheet.create({
      page: {
        padding: 10,
        width: "100%",
        backgroundColor: "#e3e3e3",
      },
      title: {
        marginBottom: 5,
        fontSize: 12,
      },
      chartContainer: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      },
      chartImage: {
        marginTop: 5,
        width: "90%",
        objectFit: "contain",
      },
    })
  );
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Get patient data
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/patients/fetch/${patientId}`)
      .then(({ data }) => {
        setPatientUser(data.PATIENT_USER);
      })
      .catch((error) => {
        console.log("Get patient data: ", error);
      });

    return () => {
      setPatientUser("");
    };
  }, [patientId]);

  const generatePDF = async () => {
    setIsDownloadingPDF(true);

    const PlotsPDF = ({ plotsImg }) => {
      return (
        <Document>
          <Page size="A4" style={styles.page}>
            <Text style={styles.title}>{`${t("view")}: ${view}`}</Text>
            {showUser && <Text style={styles.title}>{`${t("patient")}: ${patientUser}`}</Text>}
            <View style={styles.chartContainer}>
              {plotsImg.map((img, index) => (
                <Image key={index} src={img} style={styles.chartImage} />
              ))}
            </View>
          </Page>
        </Document>
      );
    };

    const capturesPlot = async () => {
      const plotsRefValues = Object.values(plotsRef);
      const filteredPlots = plotsRefValues.filter((plot) => plot.current);

      const html2canvasConfig = {
        useCORS: true,
        allowTaint: true,
        logging: true,
        backgroundColor: "#fff",
        scale: 2,
      };

      return filteredPlots.map(async (plot) => {
        const canvas = await html2canvas(plot.current, html2canvasConfig);
        return canvas.toDataURL("image/png");
      });
    };

    const plotsImg = await capturesPlot();
    const doc = <PlotsPDF plotsImg={plotsImg} />;
    const asPdf = pdf([]);
    asPdf.updateContainer(doc);
    const blob = await asPdf.toBlob();
    const fileName = showUser ? `${patientUser}_${view}.pdf` : `${view}_${moment().format("DD-MM-YYYY_HH_mm")}.pdf`;
    saveAs(blob, fileName);
    setIsDownloadingPDF(false);
  };

  return (
    <MainContainer>
      <DownloadButton onClick={generatePDF}>
        {isDownloadingPDF ? (
          <Loader height="30px" color="#c1272d" />
        ) : (
          <>
            <FileImg src={PDFImg} alt="pdf-ico" />
            <DownloadText>{t("report")} PDF</DownloadText>
          </>
        )}
      </DownloadButton>
    </MainContainer>
  );
};

export default DownloadReportPDF;
